import { ABCDCreativeSettings } from "@blisspointmedia/bpm-types/dist/pgTables/ABCDCreativeSettings";
import { ABCDScoreObject, getABCDScore } from "../ABCD/ABCDUtils";
import { getCreativeThumbnail } from "../../SingleChannel/MetricsTable/metricsTableUtils";
import { MdReviews } from "react-icons/md";
import { RadialScoreChart } from "../RadialScoreChart/RadialScoreChart";
import * as R from "ramda";
import {
  applyTVADFilters,
  getExpandedTVADDetailsContainer,
  getTVADDescriptionContainer,
  getTVADFilters,
  getTVADTableViewHeaders,
  getTVADThumbnail,
} from "./TVADCreativeListUtils";
import {
  Button,
  ButtonFrameworkVariant,
  ButtonType,
  Header,
  Img,
  OverlayTrigger,
  Tooltip,
} from "../../Components";

export enum VIEW_TYPES {
  CREATIVE_VIEW = "Creative View",
  UNIQUE_VIDEO_VIEW = "Unique Video View",
  CREATIVE_ID_VIEW = "Creative ID View",
}
export const VIEWS: View[] = R.values(VIEW_TYPES);
export type View = `${VIEW_TYPES}`;

export enum CREATIVE_MEDIATYPES_TYPES {
  ALL_CREATIVES = "All Creatives",
  LINEAR_STREAMING = "Linear/Streaming",
  COMMERCE = "Commerce",
  AUDIO = "Audio",
  SOCIAL = "Social",
  DISPLAY = "Display",
  OTHER = "Other",
}
export const CREATIVE_HEADERS = R.filter(
  header => [CREATIVE_MEDIATYPES_TYPES.LINEAR_STREAMING].includes(header),
  R.values(CREATIVE_MEDIATYPES_TYPES)
);
export type CreativeListMediaTypes = `${CREATIVE_MEDIATYPES_TYPES}`;

export enum ABCD_SECTION_TYPES {
  PACING = "Pacing",
  BRAND = "Brand",
  PRODUCT = "Product",
  SUPERS = "Supers",
  PEOPLE = "People",
  AUDIO = "Audio",
  CTA = "Call To Action",
}
export const ABCD_SECTIONS = R.values(CREATIVE_MEDIATYPES_TYPES);
export type ABCDSection = `${ABCD_SECTION_TYPES}`;

export enum HAS_SCORE_ENUM {
  HAS_SCORE = "Has Score",
  NO_SCORE = "No Score",
}

export const MEDIATYPE_PRETTY_NAME_MAP = {
  streaming: "Streaming",
  linear: "Linear TV",
  tv: "Linear TV",
};

interface GetMediatypeFiltersParams {
  mediatype: string;
  selectedToggles: Record<string, Record<string, boolean>>;
  selectedView: View;
  setSelectedToggles: (selectedToggles: Record<string, Record<string, boolean>>) => void;
}

export const getMediatypeFilters = ({
  mediatype,
  selectedToggles,
  selectedView,
  setSelectedToggles,
}: GetMediatypeFiltersParams): JSX.Element => {
  if (mediatype === "Linear/Streaming") {
    return getTVADFilters({ selectedToggles, selectedView, setSelectedToggles });
  }
  return <></>;
};

interface GetABCDScoreContainerParams {
  abcdScoreMap: Record<string, ABCDScoreObject>;
  creativeKey: string;
  creativeMap: Record<string, object>;
  runModalAvailable: boolean;
  setSelectedABCDCreative: (selectedABCDCreative: string) => void;
}

export const getABCDScoreContainer = ({
  abcdScoreMap,
  creativeKey,
  creativeMap,
  runModalAvailable,
  setSelectedABCDCreative,
}: GetABCDScoreContainerParams): JSX.Element => {
  let presentFeatures = 0;
  let totalFeatures = 0;
  const creative = creativeMap[creativeKey];
  const creativeID = creativeKey.includes(".") ? creativeKey.split(".")[0] : creativeKey;
  for (const creativeID of (creative as { creativeIDs: string[] }).creativeIDs) {
    if (abcdScoreMap && abcdScoreMap[creativeID]) {
      const score = getABCDScore(abcdScoreMap[creativeID]);
      if (score && score.presentFeatures && score.totalFeatures) {
        presentFeatures += score.presentFeatures;
        totalFeatures += score.totalFeatures;
      }
    }
  }
  if (totalFeatures > 0) {
    return (
      <div className="scoreContainer">
        <RadialScoreChart score={(100 * presentFeatures) / totalFeatures} />
      </div>
    );
  }
  return runModalAvailable ? (
    <OverlayTrigger overlay={<Tooltip>Run Best Practice Tagging</Tooltip>}>
      <Button
        className="runButton"
        design="primary"
        icon={<MdReviews />}
        onClick={() => setSelectedABCDCreative(creativeID)}
        size={"lg"}
        type={ButtonType.OUTLINED}
        variant={ButtonFrameworkVariant.LEADING_ICON}
      >
        Run
      </Button>
    </OverlayTrigger>
  ) : (
    <></>
  );
};

interface ApplyCreativeMapFiltersParams {
  abcdScoreMap: Record<string, ABCDScoreObject>;
  creativeMap: Record<string, object>;
  mediatype: string;
  selectedToggles: Record<string, Record<string, boolean>>;
}

export const applyCreativeMapFilters = ({
  abcdScoreMap,
  creativeMap: unfilteredCreativeMap,
  mediatype,
  selectedToggles,
}: ApplyCreativeMapFiltersParams): Record<string, object> => {
  const creativeMap: Record<string, object> = {};
  // Generic ABCD Score Filtering
  for (const creativeKey of R.keys(unfilteredCreativeMap)) {
    if (
      R.isNil(selectedToggles[HAS_SCORE_ENUM.HAS_SCORE]) ||
      (!selectedToggles[HAS_SCORE_ENUM.HAS_SCORE][HAS_SCORE_ENUM.HAS_SCORE] &&
        !selectedToggles[HAS_SCORE_ENUM.HAS_SCORE][HAS_SCORE_ENUM.NO_SCORE]) ||
      (selectedToggles[HAS_SCORE_ENUM.HAS_SCORE][HAS_SCORE_ENUM.HAS_SCORE] &&
        !R.isNil(abcdScoreMap[creativeKey])) ||
      (selectedToggles[HAS_SCORE_ENUM.HAS_SCORE][HAS_SCORE_ENUM.NO_SCORE] &&
        R.isNil(abcdScoreMap[creativeKey]))
    ) {
      creativeMap[creativeKey] = unfilteredCreativeMap[creativeKey];
    }
  }
  if (mediatype === CREATIVE_MEDIATYPES_TYPES.LINEAR_STREAMING) {
    return applyTVADFilters(creativeMap, selectedToggles);
  }
  return creativeMap;
};

interface GetDescriptionContainerParams {
  creativeKey: string;
  creativeMap: Record<string, object>;
  isInternal: boolean;
  selectedMediatype: CreativeListMediaTypes;
  selectedView: View;
  setSelectedEditCreative: (selectedEditCreative: object) => void;
}

export const getDescriptionContainer = ({
  creativeKey,
  creativeMap,
  isInternal,
  selectedMediatype,
  selectedView,
  setSelectedEditCreative,
}: GetDescriptionContainerParams): JSX.Element => {
  const creativeMediatype =
    selectedMediatype === CREATIVE_MEDIATYPES_TYPES.ALL_CREATIVES
      ? (creativeMap[creativeKey] as { creativeListMediatype: string }).creativeListMediatype
      : selectedMediatype;
  if (creativeMediatype === CREATIVE_MEDIATYPES_TYPES.LINEAR_STREAMING) {
    return getTVADDescriptionContainer({
      creativeKey,
      creativeMap,
      isInternal,
      selectedView: selectedView,
      setSelectedEditCreative,
    });
  }
  return getTVADDescriptionContainer({
    creativeKey,
    creativeMap,
    isInternal,
    selectedView: selectedView,
    setSelectedEditCreative,
  });
};

interface GetThumbnailParams {
  company: string;
  creativeKey: string;
  creativeMap: Record<string, object>;
  selectedMediatype: CreativeListMediaTypes;
}

export const getThumbnail = ({
  company,
  creativeKey,
  creativeMap,
  selectedMediatype,
}: GetThumbnailParams): JSX.Element => {
  const creativeMediatype =
    selectedMediatype === CREATIVE_MEDIATYPES_TYPES.ALL_CREATIVES
      ? (creativeMap[creativeKey] as { creativeListMediatype: string }).creativeListMediatype
      : selectedMediatype;
  if (creativeMediatype === CREATIVE_MEDIATYPES_TYPES.LINEAR_STREAMING) {
    return getTVADThumbnail({ company, creativeKey, creativeMap });
  }
  return (
    <Img
      src={getCreativeThumbnail(company, creativeKey)}
      unloader={<div className="creativeImageUnloader">{creativeKey}</div>}
    />
  );
};

interface GetFullDetailsContainerParams {
  abcdCreativeSettingsMap: Record<string, ABCDCreativeSettings>;
  abcdScoreMap: Record<string, ABCDScoreObject>;
  company: string;
  creativeKey: string;
  creativeMap: Record<string, object>;
  openABCDSection: string;
  selectedMediatype: CreativeListMediaTypes;
  selectedView: View;
  setOpenABCDSection: (openABCDSection: string) => void;
}

export const getExpandedDetailsContainer = ({
  abcdCreativeSettingsMap,
  abcdScoreMap,
  company,
  creativeKey,
  creativeMap,
  openABCDSection,
  selectedMediatype,
  selectedView,
  setOpenABCDSection,
}: GetFullDetailsContainerParams): JSX.Element => {
  const creativeMediatype =
    selectedMediatype === CREATIVE_MEDIATYPES_TYPES.ALL_CREATIVES
      ? (creativeMap[creativeKey] as { creativeListMediatype: string }).creativeListMediatype
      : selectedMediatype;
  if (creativeMediatype === CREATIVE_MEDIATYPES_TYPES.LINEAR_STREAMING) {
    return getExpandedTVADDetailsContainer({
      abcdCreativeSettingsMap,
      abcdScoreMap,
      company,
      creativeKey,
      creativeMap,
      openABCDSection,
      selectedView,
      setOpenABCDSection,
    });
  }
  return <div></div>;
};

interface GetTableViewHeaders {
  creativeOptions: { categoryData: { name: string }[] };
  selectedMediatype: string;
  selectedView: View;
}

export const getTableViewHeaders = ({
  creativeOptions,
  selectedMediatype,
  selectedView,
}: GetTableViewHeaders): Header[] => {
  if (selectedMediatype === CREATIVE_MEDIATYPES_TYPES.LINEAR_STREAMING) {
    return getTVADTableViewHeaders({ creativeOptions, selectedView });
  }
  return [];
};
